import { useMemo } from "react"
import { useAppSelector } from "hooks/redux"
import { useDashboardId } from "connected-components/dashboard/context/dashboard-id"
import { usePanelId } from "connected-components/dashboard/context/panel-id"
import { TrackingProperties } from "connected-components/analytics"
import {
  DEFAULT_FACTORS_VIEW_STATE,
  FACTORS_VIEW_PREFERENCE_BASE,
} from "connected-components/dashboard/store/factors-view-slice"

/**
 * Tracked properties for the "Factors View" analytics scope. Extends TrackingProperties
 * (`Record<string, unknown>`) — same pattern as AnalyticsContextProperties — so it carries the
 * index signature the provider's props sink requires while still naming the known fields.
 */
export interface FactorsViewAnalyticsProperties extends TrackingProperties {
  view: string
  selectedPeriodPerformance: string
  groupBy: string | undefined
  sortMetricId: string | undefined
  sortDirection: "ASC" | "DESC"
  navigationPath: string
  navigationDepth: number
}

/**
 * Panel-scoped factors-view analytics properties, read directly from the RTK slice.
 *
 * Reads only the factors-view slice slot — not the full useFactorsViewData value — so it doesn't
 * run core/trade/model selectors or the seed effect just to pick ~7 fields. Dashboard properties
 * (modelId, instrumentId, selectedMode, etc.) are inherited through provider nesting and must NOT
 * be extracted here.
 *
 * @returns Memoized factors view analytics properties
 */
export function useFactorsViewAnalyticsProperties(): FactorsViewAnalyticsProperties {
  const dashboardId = useDashboardId()
  const panelId = usePanelId() ?? FACTORS_VIEW_PREFERENCE_BASE
  const state = useAppSelector(
    (s) => s.dashboard.factorsView[dashboardId]?.[panelId] ?? DEFAULT_FACTORS_VIEW_STATE,
  )

  const { view, selectedPeriodPerformance, groupBy, sortMetricId, sortDirection, selectedTableLevels } = state
  const groupById = groupBy?.id
  const groupByLabel = groupBy?.label
  const navigationPath = selectedTableLevels.join("-")
  const navigationDepth = selectedTableLevels.length

  // Memoize on primitives only so the tracked-properties object stays referentially stable
  // across unrelated slice writes (e.g. datagridState changes).
  return useMemo<FactorsViewAnalyticsProperties>(
    () => ({
      view,
      selectedPeriodPerformance,
      groupBy: groupById || groupByLabel,
      sortMetricId: sortMetricId || undefined,
      sortDirection,
      navigationPath,
      navigationDepth,
    }),
    [
      view,
      selectedPeriodPerformance,
      groupById,
      groupByLabel,
      sortMetricId,
      sortDirection,
      navigationPath,
      navigationDepth,
    ],
  )
}
